import { StationIndicators } from '@app/components/stations/StationIndicators';
import { Station } from '@app/core/types/stations';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import {
  Box,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { defineMessage, useIntl } from 'react-intl';
import { StationAvatar } from '../StationAvatar';

const removeRecentStationMessage = defineMessage({
  id: 'stations.selector.removeRecent',
  defaultMessage: 'Quitar de recientes',
});

interface RecentStationListItemProps {
  readonly station: Station;
  readonly activeId: Station['id'];
  readonly isSmall: boolean;
  readonly showKeyboardIcon: boolean;
  readonly onSelectStation: (station: Station) => void;
  readonly onRemoveRecentStation: (stationId: string) => void;
}

/**
 * List item for a recently used station, with a remove action to drop it from recents.
 */
export function RecentStationListItem({
  station,
  activeId,
  isSmall,
  showKeyboardIcon,
  onSelectStation,
  onRemoveRecentStation,
}: RecentStationListItemProps) {
  const { $t } = useIntl();

  const handleRemoveClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onRemoveRecentStation(station.id);
  };

  return (
    <ListItem
      disablePadding
      sx={{
        '& .recent-station-remove-button': {
          opacity: 0,
          transition: (theme) =>
            theme.transitions.create('opacity', {
              duration: theme.transitions.duration.shortest,
            }),
        },
        '&:hover .recent-station-remove-button, &:focus-within .recent-station-remove-button': {
          opacity: 1,
        },
      }}
      secondaryAction={
        <Tooltip title={$t(removeRecentStationMessage)}>
          <IconButton
            className="recent-station-remove-button"
            edge="end"
            size="small"
            onClick={handleRemoveClick}
            sx={{ mr: 0.5 }}
          >
            <CloseIcon sx={{ fontSize: isSmall ? 16 : 18, color: 'text.secondary' }} />
          </IconButton>
        </Tooltip>
      }
    >
      <ListItemButton
        onClick={() => onSelectStation(station)}
        selected={station.id === activeId}
        dense={isSmall}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
          <ListItemAvatar sx={{ minWidth: isSmall ? 24 : 32, mr: 1 }}>
            <StationAvatar stationId={station.id} size={isSmall ? 16 : 20} />
          </ListItemAvatar>
          <ListItemText
            primary={
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                <Box component="span">{station.name}</Box>
                <StationIndicators
                  station={station}
                  variant="icon"
                  size={isSmall ? 'small' : 'medium'}
                />
              </Box>
            }
            primaryTypographyProps={{
              variant: isSmall ? 'body2' : 'body1',
            }}
          />
        </Box>
        {showKeyboardIcon && (
          <Tooltip
            title={$t({
              id: 'ui.stationsSelector.enterToGo',
              defaultMessage: 'Enter para ir',
            })}
          >
            <KeyboardReturnIcon
              sx={{
                fontSize: isSmall ? 16 : 18,
                color: 'text.secondary',
                ml: 'auto',
                mr: 3,
              }}
            />
          </Tooltip>
        )}
      </ListItemButton>
    </ListItem>
  );
}
